import usePersistentState from '@/shared/hooks/usePersistentState'
import { useState, useCallback, useMemo, useLayoutEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TSectionType, IMaintenanceEventFilters } from '../calendar.types'
import calendarUtils from '../calendar.utils'

const useCalendarCtrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Используется чтобы запоминать неделю, перед переключением режима просмотра week -> month.
  // При переключении обратно month -> week, если пользователь не изменил месяц,
  // Ему откроется неделя с которой он перешел.
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>()

  const [persistenceSelectedSection, setPersistenceSelectedSection] = usePersistentState<TSectionType>(
    'week',
    'calendar/selectedSection',
    'inMemory'
  )

  const [persistenceFilters, setPersistenceFilters] = usePersistentState<IMaintenanceEventFilters>(
    calendarUtils.getIntervalFromDate(persistenceSelectedSection),
    'calendar/filters',
    'inMemory'
  )

  const initDate = useMemo(() => calendarUtils.getDateFromSearchParams(searchParams), [searchParams])

  const activeFilters = useMemo(() => {
    if (initDate) return calendarUtils.getIntervalFromDate('week', initDate)
    return persistenceFilters
  }, [initDate, persistenceFilters])

  useLayoutEffect(() => {
    if (initDate) {
      setPersistenceSelectedSection('week')
      setPersistenceFilters(calendarUtils.getIntervalFromDate('week', initDate))
    }
  }, [])

  const handleMonthCellClick = useCallback((date: Date) => {
    setPersistenceSelectedSection('week')
    setPersistenceFilters(calendarUtils.getIntervalFromDate('week', date))
    setSearchParams((prev) => {
      prev.set('date', date.toISOString())
      return new URLSearchParams(prev)
    })
  }, [])

  const handleSectionChange = useCallback(
    (name: TSectionType) => {
      setPersistenceSelectedSection(name)
      if (name === 'month') {
        setSelectedWeekStart(persistenceFilters.date_gte)
      }
      setPersistenceFilters(
        calendarUtils.getIntervalFromDate(
          name,
          name === 'month' ? calendarUtils.getCurrentMonth('week', persistenceFilters) : selectedWeekStart
        )
      )
      setSearchParams((prev) => {
        prev.delete('date')
        return new URLSearchParams(prev)
      })
    },
    [persistenceFilters, selectedWeekStart]
  )

  const handleDateChange = useCallback(
    (dates: IMaintenanceEventFilters) => {
      if (
        persistenceSelectedSection === 'month' &&
        selectedWeekStart &&
        dates.date_gte.getMonth() !== selectedWeekStart.getMonth()
      ) {
        setSelectedWeekStart(dates.date_gte)
      }
      setPersistenceFilters(dates)
      setSearchParams((prev) => {
        prev.delete('date')
        return new URLSearchParams(prev)
      })
    },
    [persistenceSelectedSection, selectedWeekStart]
  )

  const handleTodayClick = useCallback(() => {
    setPersistenceFilters(calendarUtils.getIntervalFromDate(persistenceSelectedSection))
    setSearchParams((prev) => {
      prev.delete('date')
      return new URLSearchParams(prev)
    })
  }, [persistenceSelectedSection])

  return {
    data: {
      selectedDay: initDate,
      filters: activeFilters,
      selectedSection: initDate ? 'week' : persistenceSelectedSection,
    },
    handlers: {
      handleMonthCellClick,
      handleSectionChange,
      handleDateChange,
      handleTodayClick,
    },
  }
}

export default useCalendarCtrl
